/**
 * /programs — Athlete-side program library.
 *
 * Lists programs assigned to the athlete (with progress hydration) and lets
 * them start one or jump to the current day's workout. Each Start triggers
 * a readiness check-in (Ready / Alright / Not Great) — "Not Great" auto-
 * applies deload mode (server marks the day with readiness=not_great, the
 * runner halves reps).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FiveSHub from "@/components/programs/FiveSHub";
import {
    ArrowLeft, Loader2, Calendar, ChevronRight, Play, CheckCircle, Coffee,
    Smile, Meh, Frown, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display names + accents — kept in sync with TrainingCategories.jsx. Used
// to render the focused header when a category was selected upstream.
// Sachin 2026-02-28: clicking Strength on the category picker has to LAND
// on Strength, not the unfiltered firehose.
const CATEGORY_DISPLAY = {
    daily: { label: "Daily Workouts", accent: "#06B6D4" },
    speed: { label: "Speed", accent: "#F59E0B" },
    strength: { label: "Strength", accent: "#3B82F6" },
    stamina: { label: "Stamina", accent: "#EF4444" },
    suppleness: { label: "Suppleness", accent: "#10B981" },
    skill: { label: "Skill", accent: "#A855F7" },
};

export default function AthletePrograms() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // The category the user came from on /dashboard/workouts. When set, we
    // filter the page to that category only and show a focused header +
    // back-to-categories link. When absent (deep-link to /dashboard/programs),
    // we render the original all-categories view via FiveSHub.
    const categoryParam = (searchParams.get("category") || "").toLowerCase();
    const categoryMeta = CATEGORY_DISPLAY[categoryParam] || null;
    const [programs, setPrograms] = useState([]);
    const [busy, setBusy] = useState(true);
    const [readiness, setReadiness] = useState(null); // {program_id, week, day, workout_id, workout_title}

    useEffect(() => {
        if (loading) return;
        if (!user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/programs`, { withCredentials: true });
            setPrograms(r.data?.programs || []);
        } catch (e) {
            toast.error("Couldn't load programs", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user) load();
    }, [user]);

    // Filter to the URL-provided category when one is set. Programs without
    // a category fall through (they show on the unfiltered "all" view).
    const visiblePrograms = useMemo(() => {
        if (!categoryParam) return programs;
        return programs.filter((p) => (p.category || "").toLowerCase() === categoryParam);
    }, [programs, categoryParam]);

    const startProgram = async (p) => {
        try {
            const r = await axios.post(`${API}/programs/${p.program_id}/start`, {}, { withCredentials: true });
            toast.success("Locked in");
            await load();
            const pr = r.data?.progress;
            const firstDay = (p.weeks?.[0]?.days || []).find((d) => d.day_number === (pr?.current_day || 1));
            const ids = (firstDay?.workout_ids?.length ? firstDay.workout_ids : (firstDay?.workout_id ? [firstDay.workout_id] : []));
            if (ids.length > 0 && !firstDay.is_rest_day) {
                const titles = firstDay.workout_titles?.length ? firstDay.workout_titles : (firstDay.workout_title ? [firstDay.workout_title] : ids.map(() => "Workout"));
                setReadiness({
                    program_id: p.program_id,
                    week: pr?.current_week || 1,
                    day: pr?.current_day || 1,
                    workout_ids: ids,
                    workout_titles: titles,
                });
            }
        } catch (e) {
            toast.error("Couldn't start", { description: e?.response?.data?.detail });
        }
    };

    const openNextDay = (p) => {
        const cw = p.progress?.current_week || 1;
        const cd = p.progress?.current_day || 1;
        const week = (p.weeks || []).find((w) => w.week_number === cw);
        const day = (week?.days || []).find((d) => d.day_number === cd);
        if (!day) return;
        if (day.is_rest_day) {
            toast.success("Rest day — see you tomorrow.");
            return;
        }
        const ids = day.workout_ids?.length ? day.workout_ids : (day.workout_id ? [day.workout_id] : []);
        if (ids.length === 0) {
            toast.error("No workout scheduled for this day.");
            return;
        }
        const titles = day.workout_titles?.length ? day.workout_titles : (day.workout_title ? [day.workout_title] : ids.map(() => "Workout"));
        setReadiness({
            program_id: p.program_id,
            week: cw, day: cd,
            workout_ids: ids,
            workout_titles: titles,
        });
    };

    if (loading || !user) return null;

    return (
        <div data-testid="athlete-programs" className="min-h-screen bg-brand-obsidian text-white">
            <section className="pt-28 pb-6 border-b border-zinc-900">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <Link to="/dashboard" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Dashboard
                    </Link>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-2 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> Programs
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                        YOUR <span className="text-brand-red">ARC.</span>
                    </h1>
                    <p className="mt-3 text-sm text-zinc-300 max-w-2xl">
                        Multi-week training plans built for you by your coach. Pick up where you left off — or kick off a new block.
                    </p>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    {busy ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-zinc-300">
                            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
                        </div>
                    ) : programs.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-10 text-center text-zinc-300">
                            <Calendar className="w-7 h-7 text-zinc-700 mx-auto mb-3" />
                            No programs assigned yet. Your coach will hook you up.
                        </div>
                    ) : (
                        <FiveSHub
                            programs={programs}
                            renderProgram={(p) => {
                                const pr = p.progress || {};
                                const isStarted = pr.started || (pr.completed_days || []).length > 0;
                                const isDone = pr.is_completed;
                                return (
                                    <article
                                        key={p.program_id}
                                        data-testid={`program-card-${p.program_id}`}
                                        className="border border-zinc-800 rounded-2xl bg-zinc-950/60 overflow-hidden"
                                    >
                                        <div className="px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-800">
                                            <div>
                                                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">
                                                    {p.duration_weeks}w · {p.days_per_week}d/wk · tier: {p.tier_required}
                                                </div>
                                                <h3 className="font-display text-2xl tracking-wider">{p.title}</h3>
                                                {p.description && <p className="text-sm text-zinc-300 mt-1">{p.description}</p>}
                                            </div>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                {isDone ? (
                                                    <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-emerald-400 px-3 py-2 border border-emerald-600/40">
                                                        <CheckCircle className="w-3.5 h-3.5" /> Completed
                                                    </span>
                                                ) : isStarted ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`open-next-${p.program_id}`}
                                                        onClick={() => openNextDay(p)}
                                                        className="btn-tactical text-xs"
                                                    >
                                                        <Play className="w-3.5 h-3.5" /> Week {pr.current_week || 1} · Day {pr.current_day || 1}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        data-testid={`start-program-${p.program_id}`}
                                                        onClick={() => startProgram(p)}
                                                        className="btn-tactical text-xs"
                                                    >
                                                        <Play className="w-3.5 h-3.5" /> Start
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="px-4 sm:px-6 py-4 space-y-3">
                                            {(p.weeks || []).map((w) => {
                                                const isCurrent = w.week_number === (pr.current_week || 1);
                                                return (
                                                    <div key={w.week_number}>
                                                        <div className={`text-[10px] tracking-[0.25em] uppercase mb-1.5 ${isCurrent ? "text-brand-red" : "text-zinc-400"}`}>
                                                            Week {w.week_number}{w.week_name ? ` · ${w.week_name}` : ""}
                                                        </div>
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                                                            {(w.days || []).map((d) => {
                                                                const done = (pr.completed_days || []).some((c) => c.week === w.week_number && c.day === d.day_number);
                                                                const isCurr = isCurrent && d.day_number === (pr.current_day || 1);
                                                                const ids = d.workout_ids?.length ? d.workout_ids : (d.workout_id ? [d.workout_id] : []);
                                                                const titles = d.workout_titles?.length ? d.workout_titles : (d.workout_title ? [d.workout_title] : []);
                                                                return (
                                                                    <div
                                                                        key={d.day_number}
                                                                        data-testid={`day-${p.program_id}-${w.week_number}-${d.day_number}`}
                                                                        className={`p-2 rounded-lg border text-xs flex flex-col gap-1 min-h-[64px] ${
                                                                            done ? "border-emerald-700/40 bg-emerald-900/10" :
                                                                            isCurr ? "border-brand-red bg-brand-red/10" :
                                                                            d.is_rest_day ? "border-zinc-800 bg-zinc-900/40" :
                                                                            ids.length > 0 ? "border-zinc-800 bg-black/30" :
                                                                            "border-dashed border-zinc-800"
                                                                        }`}
                                                                    >
                                                                        <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-400">Day {d.day_number}</div>
                                                                        {d.is_rest_day ? (
                                                                            <div className="text-zinc-300 inline-flex items-center gap-1"><Coffee className="w-3 h-3" /> Rest</div>
                                                                        ) : ids.length > 0 ? (
                                                                            <div className="space-y-0.5">
                                                                                {titles.map((t, ti) => (
                                                                                    <div key={ti} className="text-white truncate" title={t}>• {t || "Workout"}</div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-zinc-500 italic">—</div>
                                                                        )}
                                                                        {done && <div className="text-[10px] text-emerald-400 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Done</div>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </article>
                                );
                            }}
                        />
                    )}
                </div>
            </section>

            {readiness && (
                <ReadinessModal
                    data={readiness}
                    onClose={() => setReadiness(null)}
                    onConfirmed={(level, workoutId) => {
                        const url = `/workout/${workoutId}?program=${readiness.program_id}&week=${readiness.week}&day=${readiness.day}&readiness=${level}`;
                        setReadiness(null);
                        navigate(url);
                    }}
                />
            )}
        </div>
    );
}


function ReadinessModal({ data, onClose, onConfirmed }) {
    // For days with multiple workouts, athlete picks one first.
    const ids = data.workout_ids || (data.workout_id ? [data.workout_id] : []);
    const titles = data.workout_titles || (data.workout_title ? [data.workout_title] : ids.map(() => "Workout"));
    const [pickedId, setPickedId] = useState(ids.length === 1 ? ids[0] : "");
    const pickedTitle = pickedId ? (titles[ids.indexOf(pickedId)] || "Workout") : "";

    const opts = [
        { value: "ready", label: "Ready to crush", icon: Smile, tone: "text-emerald-400 border-emerald-600/40 bg-emerald-900/10 hover:bg-emerald-900/20" },
        { value: "alright", label: "Alright — let's go", icon: Meh, tone: "text-amber-300 border-amber-600/40 bg-amber-900/10 hover:bg-amber-900/20" },
        { value: "not_great", label: "Not great today", icon: Frown, tone: "text-zinc-300 border-zinc-700 bg-zinc-900/40 hover:bg-zinc-900/60" },
    ];
    return (
        <div data-testid="readiness-modal" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{pickedId ? "Readiness" : "Today's lineup"}</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">{pickedTitle || "Pick a workout"}</h4>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">
                    {!pickedId ? (
                        <>
                            <p className="text-sm text-zinc-300 mb-4">
                                You have <b>{ids.length} workouts</b> on this day. Pick the one you're starting now — you can come back for the others after.
                            </p>
                            <div className="space-y-2">
                                {ids.map((id, idx) => (
                                    <button
                                        key={id}
                                        type="button"
                                        data-testid={`pick-day-workout-${id}`}
                                        onClick={() => setPickedId(id)}
                                        className="w-full text-left px-4 py-3 border rounded-xl inline-flex items-center gap-3 transition-colors border-zinc-700 hover:border-brand-red hover:bg-brand-red/5"
                                    >
                                        <span className="text-brand-red font-display text-lg">{idx + 1}</span>
                                        <span className="text-sm text-white flex-1">{titles[idx] || "Workout"}</span>
                                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-300 mb-4">How are you feeling today?</p>
                            <div className="space-y-2">
                                {opts.map((o) => (
                                    <button
                                        key={o.value}
                                        type="button"
                                        data-testid={`readiness-${o.value}`}
                                        onClick={() => onConfirmed(o.value, pickedId)}
                                        className={`w-full text-left px-4 py-3 border rounded-xl inline-flex items-center gap-3 transition-colors ${o.tone}`}
                                    >
                                        <o.icon className="w-5 h-5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-semibold text-white">{o.label}</div>
                                            {o.value === "not_great" && <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-0.5">Reps auto-reduced — log the day and move on.</div>}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                                    </button>
                                ))}
                            </div>
                            {ids.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setPickedId("")}
                                    className="mt-4 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white"
                                >← Pick a different workout</button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
